import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { ReverseChargeThresholdCard } from "@/components/ReverseChargeThresholdCard";
import useHighlightParam from "@/hooks/useHighlightParam";
import { useStickyState, useResetStickyState } from "@/hooks/useStickyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles } from "lucide-react";
import { ListSearch, matchesSearch } from "@/components/ListSearch";
import { DefaultFilterPin, useDefaultFilter } from "@/components/DefaultFilterPin";
import { toast } from "sonner";
import { ReceiptsBulkActionBar } from "@/components/ReceiptsBulkActionBar";
import { ReceiptsTable } from "@/components/ReceiptsTable";
import { DuplicateMergerDialog } from "@/components/DuplicateMergerDialog";
import { DocCompareDialog } from "@/components/DocCompareDialog";
import { UnbookAssistantDialog } from "@/components/UnbookAssistantDialog";
import { RebookDialog } from "@/components/RebookDialog";
import { ReceiptAccountDialog } from "@/components/ReceiptAccountDialog";
import { BulkMergerDialog } from "@/components/BulkMergerDialog";
import { showBulkActionToast } from "@/lib/bulkActionToast";
import { VendorMatchCard } from "@/components/VendorMatchCard";
import { ReceiptZoomDialog } from "@/components/ReceiptZoomDialog";
import { ZeroAmountDialog, MissingDateDialog } from "@/components/ReceiptBookingGuards";
import { ReceiptPayMethodDialog } from "@/components/ReceiptPayMethodDialog";
import { BillPreviewDialog, useBillPreview } from "@/components/bills/BillAttachments";
import { BillAttachmentUploadDialog } from "@/components/bills/BillAttachmentUploadDialog";
import { useCompany } from "@/context/CompanyContext";

const VAT_RATES = [
  { label: "0% (steuerfrei)", value: 0 },
  { label: "2.6% (reduziert, ab 2024)", value: 0.026 },
  { label: "3.8% (Beherbergung, ab 2024)", value: 0.038 },
  { label: "8.1% (Normalsatz, ab 2024)", value: 0.081 },
];

export function Receipts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const supplierFilter = searchParams.get("supplier") || "all";
  const companyId = localStorage.getItem("demoCompanyId");
  const { company } = useCompany();
  const [reclaimCompanies, setReclaimCompanies] = useState([]);
  useEffect(() => {
    if (company?.company_type !== "privat") { setReclaimCompanies([]); return; }
    apiClient.get("/my/companies").then((r) => setReclaimCompanies((r.data || []).filter((c) => c._id !== companyId && c.company_type !== "privat" && !c.archived).map((c) => ({ id: c._id, name: c.name })))).catch(() => setReclaimCompanies([]));
  }, [company?.company_type, companyId]);
  const [receipts, setReceipts] = useState([]);
  const attPreview = useBillPreview("receipt");
  const [attachReceipt, setAttachReceipt] = useState(null);
  const replaceReceipt = (updated) => {
    if (!updated) return;
    setReceipts((prev) => prev.map((x) => (x._id === updated._id ? { ...x, ...updated } : x)));
  };
  const [statusFilter, setStatusFilter] = useStickyState("receipts.statusFilter", "all", companyId);
  const [search, setSearch] = useState("");
  const visibleReceipts = receipts.filter((r) => matchesSearch([r.description, r.merchant_name, r.vendor, r.supplier_name, r.date, r.status, r.invoice_number, r.purpose_label, r.user_note], [r.amount, r.total_amount], search));
  const resetReceiptsView = useResetStickyState("receipts", companyId);
  const defaultFilter = useDefaultFilter("receipts.statusFilter", companyId, statusFilter, setStatusFilter);
  const [openDialog, setOpenDialog] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [contactSuggestion, setContactSuggestion] = useState(null);
  const [contactType, setContactType] = useState("supplier");
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState("");
  const [form, setForm] = useState({
    amount: "",
    currency: "CHF",
    date: "",
    description: "",
    tax_rate: 0.081,
    tax_amount: "",
    contact_id: "",
  });
  const [aiContactLoading, setAiContactLoading] = useState(false);
  const [selectedForAnalysis, setSelectedForAnalysis] = useState(null);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter100c: Bulk-Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Iter68a: Auto-Analyze beim File-Pick
  const [autoAnalyzing, setAutoAnalyzing] = useState(false);
  const [autoAnalyzeResult, setAutoAnalyzeResult] = useState(null);

  // Iter158: Foto-Vorschau (Zoom-Dialog) für einen ausgewählten Beleg
  const [zoomReceipt, setZoomReceipt] = useState(null);
  const [zoomBlobUrl, setZoomBlobUrl] = useState(null);
  // Iter158: 0.00-Warnung vor Buchung
  const [zeroAmountReceipt, setZeroAmountReceipt] = useState(null);
  // Iter159: Datum-fehlt-Warnung vor Buchung
  const [missingDateReceipt, setMissingDateReceipt] = useState(null);
  const [payDialogReceipt, setPayDialogReceipt] = useState(null);
  // Iter162: Duplikat-Merger-Dialog
  const [mergerReceiptId, setMergerReceiptId] = useState(null);
  const [compareDocs, setCompareDocs] = useState(null);
  const [compareMode, setCompareMode] = useState("dup");
  const openSelectedCompare = () => {
    const sel = receipts.filter((r) => selectedIds.has(r._id)).slice(0, 3);
    if (sel.length < 2) { toast.info("Mindestens 2 Belege anhaken"); return; }
    setCompareMode("free");
    setCompareDocs(sel.map((r) => ({
      id: r._id, fetchUrl: `/receipts/${r._id}/file`, fileName: r.file_name || r.original_filename || r.description, mime: r.mime_type,
      title: r.merchant_name || r.description || "(Beleg)", tag: "Ausgewählt",
      subtitle: `${r.date || "—"} · ${r.currency || "CHF"} ${Number(r.total_amount ?? r.amount ?? 0).toFixed(2)} · ${r.status || ""}`,
    })));
  };
  const openDuplicateCompare = async (receiptId) => {
    try {
      const r = await apiClient.get(`/receipts/${receiptId}/duplicates`);
      const items = r.data.items || [];
      if (items.length < 2) { toast.info("Keine weiteren Duplikate – dieser Beleg ist eindeutig"); return; }
      setCompareMode("dup");
      setCompareDocs(items.slice(0, 3).map((it) => ({
        id: it.id, fetchUrl: `/receipts/${it.id}/file`, fileName: it.merchant_name || it.description,
        title: it.merchant_name || it.description || "(Beleg)", accent: it.id === receiptId,
        tag: it.id === receiptId ? "Dieser Beleg" : it.is_recommended_keep ? "Empfehlung: behalten" : "Duplikat",
        subtitle: `${it.date || "—"} · ${it.currency} ${Number(it.amount || 0).toFixed(2)} · ${it.status}`,
      })));
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplikate konnten nicht geladen werden"); }
  };
  // Iter163: Rückzahl-Assistent + Bulk-Merger
  const [unbookReceipt, setUnbookReceipt] = useState(null);
  const [bulkMergerOpen, setBulkMergerOpen] = useState(false);
  // Iter168: Umbuchen (Rebook) Dialog
  const [rebookReceipt, setRebookReceipt] = useState(null);
  const [accountEditReceipt, setAccountEditReceipt] = useState(null);
  // Iter158: Reanalyze-Loading pro Beleg
  const [reanalyzingId, setReanalyzingId] = useState(null);
  // Iter158: Undo-Loading pro Beleg
  const [unbookingId, setUnbookingId] = useState(null);

  // Iter158: Foto-Zoom öffnen (lädt Original als Blob)
  const openZoom = async (receipt) => {
    setZoomReceipt(receipt);
    setZoomBlobUrl(null);
    try {
      const r = await apiClient.get(`/receipts/${receipt._id}/file`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: receipt.mime_type || "application/octet-stream",
      });
      setZoomBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("Vorschau konnte nicht geladen werden");
      setZoomReceipt(null);
    }
  };
  const closeZoom = () => {
    if (zoomBlobUrl) URL.revokeObjectURL(zoomBlobUrl);
    setZoomBlobUrl(null);
    setZoomReceipt(null);
  };

  // Iter68a: Auto-Analyze beim File-Pick — OCR + AI → Form vor-füllen
  const handleFilePick = async (selectedFile) => {
    setFile(selectedFile);
    setAutoAnalyzeResult(null);
    if (!selectedFile) return;
    const companyId = localStorage.getItem("demoCompanyId");
    if (!companyId) return;
    setAutoAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const res = await apiClient.post(
        `/companies/${companyId}/receipts/analyze-preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAutoAnalyzeResult(res.data);
      if (res.data?.success) {
        setForm((prev) => ({
          ...prev,
          amount: res.data.amount != null ? String(res.data.amount) : prev.amount,
          date: res.data.date || prev.date,
          description: res.data.description || prev.description,
          tax_rate: res.data.tax_rate != null ? res.data.tax_rate : prev.tax_rate,
          tax_amount: res.data.tax_amount != null ? String(res.data.tax_amount) : prev.tax_amount,
          currency: res.data.currency || prev.currency,
        }));
        const filledCount = [
          res.data.amount, res.data.date, res.data.description, res.data.tax_rate,
        ].filter((v) => v != null && v !== "").length;
        toast.success(
          `✨ ${filledCount} Feld${filledCount === 1 ? "" : "er"} automatisch erkannt`
          + (res.data.vendor ? ` (${res.data.vendor})` : ""),
        );
      } else if (res.data?.error) {
        toast.error(res.data.error);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail) toast.error(`Auto-Analyse fehlgeschlagen: ${detail}`);
    } finally {
      setAutoAnalyzing(false);
    }
  };


  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({});

  const loadReceipts = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const res = await apiClient.get(`/companies/${companyId}/receipts`, {
        params,
      });
      let list = res.data || [];
      // Vendor-Hygiene URL-Filter (vom Dashboard verlinkt)
      if (supplierFilter === "missing") {
        list = list.filter((r) => !r.supplier_id);
      } else if (supplierFilter === "lowscore") {
        list = list.filter(
          (r) =>
            !r.supplier_id &&
            r.vendor_match &&
            r.vendor_match.vendor_name &&
            !r.vendor_match.auto_match
        );
      }
      setReceipts(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [companyId, statusFilter, supplierFilter]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  useHighlightParam(receipts, (r) => r._id || r.id);

  useEffect(() => {
    if (!companyId) return;
    const loadCompanyDefaults = async () => {
      try {
        const res = await apiClient.get(`/companies/${companyId}`);
        const rate = res.data?.default_vat_rate;
        if (rate !== null && rate !== undefined) {
          setForm((prev) => ({
            ...prev,
            tax_rate: Number(rate) / 100,
          }));
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadCompanyDefaults();
  }, [companyId]);


  const calcAmounts = () => {
    const gross = parseFloat(form.amount) || 0;
    const rate = form.tax_rate || 0;
    let tax = form.tax_amount ? parseFloat(form.tax_amount) || 0 : gross * rate;
    if (tax < 0) tax = 0;
    const net = gross - tax;
    return { net, tax, gross };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    const formData = new FormData();
    formData.append("amount", String(form.amount));
    formData.append("currency", form.currency);
    formData.append("date", form.date);
    formData.append("description", form.description);
    formData.append("tax_rate", String(form.tax_rate * 100));
    if (form.tax_amount) {
      formData.append("tax_amount", String(form.tax_amount));
    }
    if (file) formData.append("file", file);

    try {
      const res = await apiClient.post(`/companies/${companyId}/receipts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const created = res.data;
      setOpenDialog(false);
      setForm({ amount: "", currency: "CHF", date: "", description: "", tax_rate: 0.081, tax_amount: "", contact_id: "" });
      setFile(null);
      setAutoAnalyzeResult(null);
      await loadReceipts();

      // Iter232: KI-Anlagen-Erkennung — Hinweis direkt nach dem Upload
      if (created.asset_suggestion?.detected) {
        toast.info(
          `🏭 KI: Dieser Beleg sieht nach ${created.asset_suggestion.category_label} aus — in der Liste "Als Anlage anlegen" klicken für Auto-Abschreibung`,
          { duration: 9000 }
        );
      }

      // Iter145 (C): Toast wenn KI eine passende Bank-Buchung gefunden hat
      try {
        const sug = await apiClient.get(
          `/companies/${companyId}/match-suggestions?status=pending`
        );
        const forThis = (sug.data.items || []).filter((x) => x.source_id === created._id);
        if (forThis.length > 0) {
          toast.success(
            `✨ ${forThis.length} passende Bank-Buchung(en) gefunden — im Dashboard bestätigen`,
            { duration: 6000 }
          );
        }
      } catch { /* silent */ }

      // Nach Anlage des Belegs prüfen, ob ein neuer Kontakt vorgeschlagen werden soll
      if (created && !created.contact_id) {
        await fetchContactSuggestion(created);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchContactSuggestion = async (receipt) => {
    if (!companyId || !receipt?._id) return;
    setAiContactLoading(true);
    setContactError("");
    try {
      const res = await apiClient.get(
        `/companies/${companyId}/receipts/${receipt._id}/contact-suggestion`,
      );
      const suggestion = res.data;
      if (suggestion.should_create) {
        setSelectedReceipt(receipt);
        setContactSuggestion(suggestion);
        setContactType(suggestion.type_hint || "supplier");
        setContactDialogOpen(true);
      }
    } catch (e) {
      console.error(e);
      setContactError(e.response?.data?.detail || "Fehler beim Laden des Kontaktvorschlags.");
    } finally {
      setAiContactLoading(false);
    }
  };

  const handleCreateContactFromSuggestion = async () => {
    if (!companyId || !selectedReceipt || !contactSuggestion) return;
    setContactSaving(true);
    setContactError("");
    try {
      const payload = {
        type: contactType,
        name: contactSuggestion.suggested_name || selectedReceipt.description,
        email: null,
        address: null,
        postal_code: null,
        city: null,
        vat_id: null,
      };
      const res = await apiClient.post(`/companies/${companyId}/contacts`, payload);
      const newContact = res.data;
      await apiClient.put(`/receipts/${selectedReceipt._id}`, {
        contact_id: newContact._id,
      });
      setContactDialogOpen(false);
      setSelectedReceipt(null);
      setContactSuggestion(null);
      await loadReceipts();
    } catch (e) {
      console.error(e);
      setContactError(e.response?.data?.detail || "Fehler beim Anlegen des Kontakts.");
    } finally {
      setContactSaving(false);
    }
  };

  const handleAnalyze = async (receipt) => {
    if (!companyId || !receipt?._id) return;
    try {
      setSelectedForAnalysis(receipt);
      const res = await apiClient.post(
        `/companies/${companyId}/receipts/${receipt._id}/analyze`,
      );
      const updated = res.data;
      // Belege neu laden, damit Status/Analyse sichtbar wird
      await loadReceipts();
      setSelectedForAnalysis(updated);
    } catch (e) {
      console.error(e);
    }
  };

  const handlePostReceipt = async (receiptId, opts = {}) => {
    const receipt = receipts.find((r) => r._id === receiptId);
    // Iter158: 0.00-CHF-Warnung — verhindert versehentliche Nullbuchungen
    if (!opts.skipZeroCheck) {
      const amt = Number(receipt?.amount || 0);
      if (!amt || amt <= 0) {
        setZeroAmountReceipt(receipt || { _id: receiptId, amount: amt });
        return;
      }
    }
    // Iter159: Datum-Guard — Backend blockt sowieso; Frontend zeigt freundlichen Dialog
    if (!opts.skipDateCheck) {
      const dateStr = receipt?.date;
      const hasDate = dateStr && String(dateStr).trim().length >= 8;
      if (!hasDate) {
        setMissingDateReceipt(receipt || { _id: receiptId });
        return;
      }
    }
    // Iter265: "Wie wurde bezahlt?" — Dialog öffnen statt direkt zu buchen
    setPayDialogReceipt(receipt || { _id: receiptId });
  };

  // Iter265: tatsächliche Buchung nach Zahlungsart-Wahl (cash | private)
  const doPostReceipt = async (receiptId, paymentMethod, note) => {
    try {
      await apiClient.post(`/receipts/${receiptId}/post`, {
        payment_method: paymentMethod,
        note: note || undefined,
      });
      toast.success(paymentMethod === "cash"
        ? "Beleg bar verbucht — an Kasse 1000."
        : "Beleg verbucht — an Kontokorrent Inhaber 2850.");
      setPayDialogReceipt(null);
      loadReceipts();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Buchung fehlgeschlagen.";
      if (String(msg).includes("Beleg-Datum")) {
        setPayDialogReceipt(null);
        setMissingDateReceipt({ _id: receiptId });
      } else {
        toast.error(msg);
      }
    }
  };

  // Iter158: Rückverbuchen — jetzt (Iter163) via Assistent-Dialog
  const handleRestore = async (receiptId) => {
    try {
      await apiClient.post(`/receipts/${receiptId}/restore`);
      toast.success("Beleg aus dem Papierkorb wiederhergestellt");
      loadReceipts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wiederherstellen fehlgeschlagen");
    }
  };

  const handleUnarchive = async (receiptId) => {
    try {
      const res = await apiClient.post(`/receipts/${receiptId}/unarchive?company_id=${companyId}`);
      toast.success(res.data.double_check_passed
        ? "Beleg wiederhergestellt — Doppel-Kontrolle ✅"
        : "Beleg wiederhergestellt");
      loadReceipts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wiederherstellen fehlgeschlagen");
    }
  };

  // Iter232: Anlage aus KI-Vorschlag erstellen (Auto-Abschreibung inklusive)
  const handleCreateAsset = async (receipt) => {
    try {
      const res = await apiClient.post(`/receipts/${receipt._id}/create-asset`, {});
      toast.success(res.data.message || "Anlage erstellt — Auto-Abschreibung aktiv");
      loadReceipts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlage konnte nicht erstellt werden");
    }
  };


  const handleUnbookReceipt = (receiptId) => {
    const r = receipts.find((x) => x._id === receiptId);
    if (r) setUnbookReceipt(r);
  };

  // Iter158: Beleg neu analysieren (mit Vision-LLM Fallback)
  const handleReanalyze = async (receiptId) => {
    setReanalyzingId(receiptId);
    const before = receipts.find((x) => x._id === receiptId);
    try {
      const r = await apiClient.post(`/receipts/${receiptId}/reanalyze`);
      const amt = Number(r.data?.amount || 0);
      const nowCn = !!r.data?.is_credit_note;
      if (before && !!before.is_credit_note !== nowCn) {
        toast.success(nowCn ? "Belegart korrigiert: Gutschrift (Geld kommt zurück)" : "Belegart korrigiert: normaler Beleg (Aufwand) – keine Gutschrift mehr",
          { description: r.data?.credit_note_check || undefined, duration: 7000 });
      } else if (amt > 0) {
        toast.success(`Neu analysiert — Betrag: ${amt.toFixed(2)} CHF`, { description: r.data?.credit_note_check || undefined });
      } else {
        toast.info("Analyse abgeschlossen — bitte manuell prüfen.");
      }
      await loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Neu-Analyse fehlgeschlagen.");
    } finally {
      setReanalyzingId(null);
    }
  };

  // Iter100c: Bulk-Selection Handlers
  const toggleSelect = (rid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    const visibleIds = receipts.map((r) => r._id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds(
      allSelected
        ? new Set()
        : new Set([...selectedIds, ...visibleIds])
    );
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `${selectedIds.size} Beleg(e) aus der Belegliste entfernen? Sie gehen zurück in den Posteingang (Bestätigung wird aufgehoben). Bereits verbuchte werden übersprungen.`
      )
    )
      return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/receipts/bulk-delete`,
        { receipt_ids: Array.from(selectedIds) }
      );
      const { succeeded, skipped } = r.data;
      toast.success(`${succeeded} Beleg(e) zurück im Posteingang${skipped ? ` · ${skipped} übersprungen (verbucht)` : ""}`,
        { action: { label: "Zum Posteingang", onClick: () => navigate("/inbox") } });
      clearSelection();
      loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zurückschicken fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkStatus = async (newStatus) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/receipts/bulk-status`,
        { receipt_ids: Array.from(selectedIds), status: newStatus }
      );
      const { succeeded, skipped, undo_token } = r.data;
      showBulkActionToast({
        message: `${succeeded} als "${
          newStatus === "reviewed" ? "geprüft" : "ungeprüft"
        }" markiert${skipped ? ` · ${skipped} übersprungen` : ""}`,
        undoToken: undo_token,
        companyId,
        onUndo: loadReceipts,
      });
      clearSelection();
      loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-Update fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Iter161: Bulk-Rückverbuchen
  const runBulkUnbook = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `${selectedIds.size} Beleg(e) zurückbuchen? Journal-Einträge werden gelöscht, Status → 'Offen'. Nicht-verbuchte Belege werden übersprungen.`
      )
    ) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/receipts/bulk-unbook`,
        { receipt_ids: Array.from(selectedIds) }
      );
      const { succeeded, skipped, undo_token } = r.data;
      showBulkActionToast({
        message: `${succeeded} zurückgebucht${skipped ? ` · ${skipped} übersprungen` : ""}`,
        undoToken: undo_token,
        companyId,
        onUndo: loadReceipts,
      });
      clearSelection();
      loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Rückverbuchen fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Iter161: Bulk-Neu-Analysieren
  const runBulkReanalyze = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `${selectedIds.size} Beleg(e) neu analysieren? Das kann etwas dauern. Verbuchte Belege werden übersprungen.`
      )
    ) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/receipts/bulk-reanalyze`,
        { receipt_ids: Array.from(selectedIds) }
      );
      const { succeeded, skipped } = r.data;
      toast.success(
        `${succeeded} neu analysiert${skipped ? ` · ${skipped} übersprungen` : ""}`
      );
      clearSelection();
      loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Neu-Analyse fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };



  const refreshVendorMatch = async (receipt) => {
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/receipts/${receipt._id}/vendor-match`
      );
      setSelectedForAnalysis((prev) =>
        prev && prev._id === receipt._id ? { ...prev, vendor_match: r.data } : prev
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Match-Refresh fehlgeschlagen.");
    }
  };

  const assignSupplier = async (receipt, body) => {
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/receipts/${receipt._id}/assign-supplier`,
        body
      );
      toast.success(
        body.create_new
          ? `Lieferant "${body.create_new.name}" angelegt und zugewiesen.`
          : "Lieferant zugewiesen."
      );
      // Belegen-State updaten
      setSelectedForAnalysis((prev) =>
        prev && prev._id === receipt._id
          ? { ...prev, supplier_id: r.data.supplier_id }
          : prev
      );
      await refreshVendorMatch(receipt);
      await loadReceipts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zuweisen fehlgeschlagen.");
    }
  };

  const createSupplierFromAnalysis = async (receipt) => {
    const ar = receipt?.analysis_result || {};
    const name = (ar.merchant_name || "").trim();
    if (!name) {
      toast.error("Kein Lieferantenname in der Analyse erkannt.");
      return;
    }
    const create_new = {
      name,
      address: ar.merchant_address || ar.address || null,
      postal_code: ar.merchant_postal_code || null,
      city: ar.merchant_city || null,
      iban: ar.iban || null,
      vat_id: ar.merchant_vat_id || ar.vat_id || null,
    };
    await assignSupplier(receipt, { create_new });
  };

  const saveAsVendorRule = async (receipt) => {
    const ar = receipt?.analysis_result || {};
    const pattern =
      (ar.merchant_name || "").trim() ||
      (receipt?.description || "").split(" ").slice(0, 3).join(" ").trim();
    const accountCode = ar.suggested_account;
    if (!pattern || pattern.length < 2 || !accountCode) {
      toast.error("Pattern oder Konto fehlt für die Regel.");
      return;
    }
    // Account-ID ermitteln (suggested_account ist Code, nicht ID)
    try {
      const accountsRes = await apiClient.get(`/companies/${companyId}/accounts`);
      const found = (accountsRes.data || []).find(
        (a) => a.code === accountCode || a._id === accountCode
      );
      if (!found) {
        toast.error(`Konto "${accountCode}" nicht gefunden.`);
        return;
      }
      const proposedPattern = window.prompt(
        "Vendor / Beleg-Text als Pattern (Substring, case-insensitive):",
        pattern
      );
      if (!proposedPattern || proposedPattern.length < 2) return;
      await apiClient.post(`/companies/${companyId}/vendor-rules`, {
        vendor_pattern: proposedPattern,
        account_id: found._id,
      });
      toast.success(
        `Regel gespeichert: "${proposedPattern}" → ${found.code} ${found.name}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regel-Speichern fehlgeschlagen.");
    }
  };

  const { net, tax, gross } = calcAmounts();
  const navigate = useNavigate();

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <ReverseChargeThresholdCard companyId={companyId} />

      {/* Iter85: Hinweis-Banner — Upload erfolgt im Posteingang */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm" data-testid="receipts-inbox-hint">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="text-indigo-600 text-base flex-shrink-0">📥</span>
          <div className="min-w-0">
            <p className="font-medium text-indigo-900">Neue Belege gehören in den Posteingang</p>
            <p className="text-xs text-indigo-700 mt-0.5">
              Diese Seite zeigt deine <b>bereits bestätigten Belege</b> als Archiv.
              Upload mit KI-Klassifikation läuft zentral im Posteingang.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => navigate("/inbox")}
          className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
          data-testid="receipts-goto-inbox"
        >
          Zum Posteingang →
        </Button>
      </div>
      {supplierFilter !== "all" && (
        <div
          className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm"
          data-testid="vendor-filter-banner"
        >
          <div className="text-amber-900">
            <strong>Filter aktiv:</strong>{" "}
            {supplierFilter === "missing"
              ? "Belege ohne zugewiesenen Lieferanten"
              : "Belege mit niedrigem Match-Score (<85%)"}
            {" "}({receipts.length})
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSearchParams({})}
            data-testid="vendor-filter-clear"
          >
            Filter aufheben
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Belege
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Erfassen und überblicken Sie Ihre Quittungen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ListSearch value={search} onChange={setSearch} count={visibleReceipts.length} testid="receipts-search" className="w-72" placeholder="Suchen: Lieferant, Text, Betrag, Datum …" />
          <Select
            value={statusFilter}
            onValueChange={setStatusFilter}
          >
            <SelectTrigger
              className="w-40"
              data-testid="receipts-status-filter"
            >
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="unreviewed">Offen</SelectItem>
              <SelectItem value="suggested">Mit Vorschlag</SelectItem>
              <SelectItem value="booked">Verbucht</SelectItem>
              <SelectItem value="archived">Archiviert</SelectItem>
              <SelectItem value="deleted">🗑 Papierkorb</SelectItem>
            </SelectContent>
          </Select>

          <DefaultFilterPin
            value={statusFilter}
            label={{ all: "Alle", unreviewed: "Offen", suggested: "Mit Vorschlag", booked: "Verbucht", archived: "Archiviert", deleted: "Papierkorb" }[statusFilter] || statusFilter}
            defaultValue={defaultFilter.defaultValue}
            isDefault={defaultFilter.isDefault}
            onSave={defaultFilter.save}
            testId="receipts-default-filter-pin"
          />

          {statusFilter !== (defaultFilter.defaultValue || "all") && (
            <button
              type="button"
              onClick={() => { setStatusFilter(defaultFilter.defaultValue || "all"); resetReceiptsView(); }}
              className="text-[11px] text-slate-500 hover:text-slate-700 underline"
              data-testid="receipts-reset-view"
              title="Filter zurücksetzen"
            >
              ↻ Standard
            </button>
          )}

          <Button
            onClick={() => setBulkMergerOpen(true)}
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="bulk-merger-button"
            title="KI-gestütztes Auto-Mergen aller Beleg-Duplikate"
          >
            🧹 Duplikate auto-mergen
          </Button>

          <Button
            onClick={() => navigate("/inbox")}
            className="rounded-full bg-slate-900 text-slate-50 px-5"
            data-testid="add-receipt-button"
          >
            📥 Im Posteingang erfassen
          </Button>

          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogContent data-testid="add-receipt-dialog">
              <DialogHeader>
                <DialogTitle>Neuen Beleg erfassen</DialogTitle>
              </DialogHeader>
              <form
                className="space-y-4"
                onSubmit={handleCreate}
                data-testid="add-receipt-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="amount">Betrag (Brutto)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      required
                      data-testid="receipt-amount-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="currency">Währung</Label>
                    <Select
                      value={form.currency}
                      onValueChange={(value) => setForm({ ...form, currency: value })}
                    >
                      <SelectTrigger
                        id="currency"
                        data-testid="receipt-currency-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHF">CHF</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="date">Datum</Label>
                    <SwissDateField
                      id="date"
                      
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                      data-testid="receipt-date-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="vat-rate">MWST-Satz</Label>
                    <Select
                      value={String(form.tax_rate)}
                      onValueChange={(value) => setForm({ ...form, tax_rate: Number(value) })}
                    >
                      <SelectTrigger
                        id="vat-rate"
                        data-testid="receipt-vat-rate-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VAT_RATES.map((rate) => (
                          <SelectItem
                            key={rate.value}
                            value={String(rate.value)}
                            data-testid={`receipt-vat-rate-option-${rate.value}`}
                          >
                            {rate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="tax-amount">MWST-Betrag (optional)</Label>
                    <Input
                      id="tax-amount"
                      type="number"
                      step="0.01"
                      value={form.tax_amount}
                      onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
                      data-testid="receipt-tax-amount-input"
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label htmlFor="description">Beschreibung</Label>
                    <Input
                      id="description"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      required
                      data-testid="receipt-description-input"
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label htmlFor="file">Quittung (Datei oder Foto)</Label>
                    <Input
                      id="file"
                      type="file"
                      accept="image/*,application/pdf"
                      capture="environment"
                      onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
                      disabled={autoAnalyzing}
                      data-testid="receipt-file-input"
                    />
                    {autoAnalyzing && (
                      <div className="flex items-center gap-2 text-xs text-emerald-700 mt-1" data-testid="receipt-auto-analyzing">
                        <span className="inline-block w-3 h-3 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></span>
                        ✨ Erkenne Betrag, Datum & MWST automatisch…
                      </div>
                    )}
                    {autoAnalyzeResult?.success && !autoAnalyzing && (
                      <div className="text-xs text-emerald-700 mt-1" data-testid="receipt-auto-analyze-success">
                        ✅ Felder vorausgefüllt
                        {autoAnalyzeResult.vendor && ` · ${autoAnalyzeResult.vendor}`}
                        {autoAnalyzeResult.ocr_chars > 0 && ` · ${autoAnalyzeResult.ocr_chars} Zeichen erkannt`}
                        {" · "}<span className="text-slate-500">bitte vor Speichern prüfen</span>
                      </div>
                    )}
                    {autoAnalyzeResult && !autoAnalyzeResult.success && !autoAnalyzing && (
                      <div className="text-xs text-amber-700 mt-1" data-testid="receipt-auto-analyze-failed">
                        ⚠️ {autoAnalyzeResult.error || "Auto-Erkennung fehlgeschlagen"}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500">
                      Auf dem Handy/iPad öffnet sich direkt die Kamera. Tipp: Beleg gerade halten,
                      gut beleuchten. Alternativ: <a href="/scan" className="underline" data-testid="receipts-mobile-scan-link">Mobile-Scan-Modus</a>.
                    </p>
                  </div>
                </div>

                {/* Bulk Upload Drag & Drop */}
                <details className="mt-4 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-3" data-testid="bulk-upload-section">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-800 select-none">
                    📦 Bulk-Upload: mehrere Belege gleichzeitig (Drag & Drop)
                  </summary>
                  <div className="mt-3 space-y-2">
                    <div
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-emerald-50", "border-emerald-300"); }}
                      onDragLeave={(e) => e.currentTarget.classList.remove("bg-emerald-50", "border-emerald-300")}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove("bg-emerald-50", "border-emerald-300");
                        const dropped = Array.from(e.dataTransfer.files || []);
                        setBulkFiles((prev) => [...prev, ...dropped.slice(0, 50)]);
                      }}
                      className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500 transition-colors"
                      data-testid="bulk-upload-dropzone"
                    >
                      Dateien hierhin ziehen oder
                      <label className="ml-1 underline cursor-pointer text-emerald-700">
                        durchsuchen
                        <input
                          type="file"
                          multiple
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => setBulkFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
                          data-testid="bulk-upload-input"
                        />
                      </label>
                      <div className="text-[11px] mt-1">Max. 50 Belege · je 8 MB · JPG/PNG/PDF</div>
                    </div>

                    {bulkFiles.length > 0 && (
                      <div className="space-y-1" data-testid="bulk-upload-queue">
                        <div className="text-xs font-semibold text-slate-700">Warteschlange: {bulkFiles.length}</div>
                        <ul className="max-h-40 overflow-auto space-y-1">
                          {bulkFiles.map((f, i) => (
                            <li key={`${f.name}-${i}`} className="flex items-center justify-between text-[11px] bg-white rounded px-2 py-1 border border-slate-200">
                              <span className="truncate max-w-[260px]">{f.name} <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span></span>
                              <span className="text-slate-500">{bulkProgress[i] || ""}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="flex gap-2">
                          <Button
                            type="button" size="sm" variant="outline" className="rounded-full"
                            onClick={() => { setBulkFiles([]); setBulkProgress({}); }}
                            disabled={bulkUploading}
                            data-testid="bulk-upload-clear"
                          >Leeren</Button>
                          <Button
                            type="button" size="sm"
                            className="rounded-full bg-emerald-600 text-white"
                            disabled={bulkUploading || bulkFiles.length === 0}
                            data-testid="bulk-upload-start"
                            onClick={async () => {
                              setBulkUploading(true);
                              setBulkProgress({});
                              const today = new Date().toISOString().slice(0, 10);
                              let successCount = 0;
                              for (let i = 0; i < bulkFiles.length; i++) {
                                setBulkProgress((p) => ({ ...p, [i]: "⏳" }));
                                try {
                                  const fd = new FormData();
                                  fd.append("amount", "0");
                                  fd.append("currency", "CHF");
                                  fd.append("date", today);
                                  fd.append("description", `Bulk-Upload ${today} #${i + 1}`);
                                  fd.append("file", bulkFiles[i]);
                                  await apiClient.post(`/companies/${companyId}/receipts`, fd, {
                                    headers: { "Content-Type": "multipart/form-data" },
                                  });
                                  setBulkProgress((p) => ({ ...p, [i]: "✅" }));
                                  successCount += 1;
                                } catch (err) {
                                  setBulkProgress((p) => ({ ...p, [i]: "❌" }));
                                }
                              }
                              setBulkUploading(false);
                              toast.success(`${successCount} / ${bulkFiles.length} Belege verarbeitet.`);
                              loadReceipts();
                            }}
                          >
                            {bulkUploading ? "Lade hoch..." : `${bulkFiles.length} hochladen`}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </details>

                <div className="border-t pt-3 mt-3 text-sm text-slate-700 space-y-1">
                  <div className="flex justify-between">
                    <span>Netto (vor MWST)</span>
                    <span data-testid="receipt-net-amount">{net.toFixed(2)} {form.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>MWST ({(form.tax_rate * 100).toFixed(1)}%)</span>
                    <span data-testid="receipt-tax-computed">{tax.toFixed(2)} {form.currency}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Brutto (gesamt)</span>
                    <span data-testid="receipt-gross-amount">{gross.toFixed(2)} {form.currency}</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOpenDialog(false)}
                    data-testid="add-receipt-cancel-button"
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-full bg-slate-900 text-slate-50 px-5"
                    data-testid="add-receipt-submit-button"
                  >
                    Speichern
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card
        className="p-4 border border-slate-200 bg-white shadow-sm"
        data-testid="receipts-table-card"
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">
            Alle Belege
          </h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="rounded-full text-xs"
            >
              Bankabgleich (in Arbeit)
            </Button>
          </div>

          {loading && (
            <span
              className="text-xs text-slate-400"
              data-testid="receipts-loading-indicator"
            >
              Lade ...
            </span>
          )}
        </div>

        {/* Iter100c: Bulk-Action Bar */}
        <ReceiptsBulkActionBar
          selectedCount={selectedIds.size}
          bulkBusy={bulkBusy}
          onMarkReviewed={() => runBulkStatus("reviewed")}
          onMarkUnreviewed={() => runBulkStatus("unreviewed")}
          onReanalyze={runBulkReanalyze}
          onUnbook={runBulkUnbook}
          onDelete={runBulkDelete}
          onClear={clearSelection}
          onCompare={openSelectedCompare}
        />

        <ReceiptsTable
          receipts={visibleReceipts}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAllVisible}
          onZoom={openZoom}
          attachmentActions={{ onOpen: attPreview.open, loading: attPreview.loading, onAddAttachment: setAttachReceipt }}
          onOpenMerger={setMergerReceiptId}
          onCompareDuplicates={openDuplicateCompare}
          onPost={handlePostReceipt}
          onReanalyze={handleReanalyze}
          reanalyzingId={reanalyzingId}
          onRebook={setRebookReceipt}
          onUnbook={handleUnbookReceipt}
          onToInboxBooked={(r) => setUnbookReceipt({ ...r, _toInbox: true })}
          reclaimCompanies={reclaimCompanies}
          onReclaimed={() => loadReceipts()}
          unbookingId={unbookingId}
          onUnarchive={handleUnarchive}
          onRestore={handleRestore}
          onCreateAsset={handleCreateAsset}
          onEditAccount={setAccountEditReceipt}
          onToInbox={async (r) => {
            if (!window.confirm(`«${r.description || "Beleg"}» zurück in den Posteingang schicken? Der Beleg verschwindet aus der Belegliste und kann dort neu zugeordnet werden.`)) return;
            try {
              await apiClient.post(`/receipts/${r._id}/to-inbox`);
              toast.success("Beleg ist wieder im Posteingang", { action: { label: "Zum Posteingang", onClick: () => navigate("/inbox") } });
              loadReceipts();
            } catch (e) { toast.error(e?.response?.data?.detail || "Konnte nicht zurückgeschickt werden"); }
          }}
          onToggleCreditNote={async (r, flag) => {
            try {
              await apiClient.put(`/receipts/${r._id}`, { is_credit_note: flag });
              toast.success(flag ? "Als Gutschrift markiert" : `«${r.description || "Beleg"}» ist jetzt ein normaler Beleg (Aufwand)`);
              loadReceipts();
            } catch (e) { toast.error(e?.response?.data?.detail || "Änderung fehlgeschlagen"); }
          }}
        />
      </Card>

      <ReceiptAccountDialog
        receipt={accountEditReceipt}
        companyId={companyId}
        onClose={() => setAccountEditReceipt(null)}
        onSaved={loadReceipts}
      />

      {/* Contact Suggestion Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent data-testid="contact-suggestion-dialog">
          <DialogHeader>
            <DialogTitle>Neuen Kontakt anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Für diesen Beleg wurde kein Kontakt gefunden. Möchten Sie einen neuen Kontakt anlegen?
            </p>
            
            {contactSuggestion && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="contact-name">Name</Label>
                  <Input
                    id="contact-name"
                    value={contactSuggestion.suggested_name || selectedReceipt?.description || ""}
                    readOnly
                    className="bg-slate-50"
                    data-testid="contact-suggestion-name"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="contact-type">Typ</Label>
                  <Select
                    value={contactType}
                    onValueChange={setContactType}
                  >
                    <SelectTrigger
                      id="contact-type"
                      data-testid="contact-suggestion-type"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supplier">Lieferant</SelectItem>
                      <SelectItem value="customer">Kunde</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {contactError && (
              <div className="text-sm text-red-600" data-testid="contact-error">
                {contactError}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setContactDialogOpen(false)}
                disabled={contactSaving}
                data-testid="contact-suggestion-cancel"
              >
                Abbrechen
              </Button>
              <Button
                type="button"
                onClick={handleCreateContactFromSuggestion}
                disabled={contactSaving}
                className="rounded-full bg-slate-900 text-slate-50 px-5"
                data-testid="contact-suggestion-create"
              >
                {contactSaving ? "Speichere..." : "Kontakt anlegen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analysis Dialog */}
      <Dialog open={!!selectedForAnalysis} onOpenChange={() => setSelectedForAnalysis(null)}>
        <DialogContent data-testid="receipt-analysis-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Analyse-Ergebnis</DialogTitle>
          </DialogHeader>
          {selectedForAnalysis?.analysis_result ? (
            <Tabs defaultValue="overview">
              <TabsList className="mb-2">
                <TabsTrigger value="overview">Übersicht</TabsTrigger>
                {selectedForAnalysis.ocr_text && (
                  <TabsTrigger value="ocr">OCR-Text</TabsTrigger>
                )}
                <TabsTrigger value="raw">Rohdaten</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="space-y-1 text-sm">
                {selectedForAnalysis.ocr_text && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs font-medium text-blue-900 mb-1">
                      ✓ OCR erfolgreich durchgeführt
                    </p>
                    <p className="text-xs text-blue-700">
                      Text wurde automatisch aus dem hochgeladenen Bild extrahiert.
                    </p>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Händler</span>
                  <span className="font-mono">
                    {selectedForAnalysis.analysis_result.merchant_name || "(unbekannt)"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Betrag</span>
                  <span className="font-mono">
                    {selectedForAnalysis.analysis_result.total_amount ?? "-"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Währung</span>
                  <span className="font-mono">
                    {selectedForAnalysis.analysis_result.currency || selectedForAnalysis.currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Datum</span>
                  <span className="font-mono">
                    {selectedForAnalysis.analysis_result.transaction_date || selectedForAnalysis.date}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Vorgeschlagenes Konto</span>
                  <span className="font-mono">
                    {selectedForAnalysis.analysis_result.suggested_account || "(kein Vorschlag)"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Vertrauen</span>
                  <span className="font-mono">
                    {selectedForAnalysis.analysis_result.confidence_score != null
                      ? selectedForAnalysis.analysis_result.confidence_score.toFixed(2)
                      : "-"}
                  </span>
                </div>
              </TabsContent>
              {selectedForAnalysis.ocr_text && (
                <TabsContent value="ocr">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">
                      Extrahierter Text aus Belegbild:
                    </p>
                    <pre className="text-xs bg-slate-100 text-slate-900 p-3 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap">
                      {selectedForAnalysis.ocr_text}
                    </pre>
                  </div>
                </TabsContent>
              )}
              <TabsContent value="raw">
                <pre className="text-xs bg-slate-950 text-slate-50 p-2 rounded-lg overflow-auto max-h-64">
                  {JSON.stringify(selectedForAnalysis.analysis_result, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          ) : (
            <p className="text-sm text-slate-500">
              Für diesen Beleg liegt noch kein Analyse-Ergebnis vor.
            </p>
          )}
          {selectedForAnalysis?.analysis_result?.merchant_name && (
            <VendorMatchCard
              receipt={selectedForAnalysis}
              onRefresh={() => refreshVendorMatch(selectedForAnalysis)}
              onAssign={(contact_id) => assignSupplier(selectedForAnalysis, { contact_id })}
              onCreate={() => createSupplierFromAnalysis(selectedForAnalysis)}
            />
          )}
          {selectedForAnalysis?.analysis_result?.suggested_account && (
            <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-slate-600 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                <span>
                  Häufiger Lieferant?{" "}
                  <span className="text-slate-400">
                    Speichere "{selectedForAnalysis.analysis_result.merchant_name || "..."}" als Auto-Lern-Regel.
                  </span>
                </span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => saveAsVendorRule(selectedForAnalysis)}
                data-testid="receipt-save-vendor-rule-button"
                title="Merkt sich: Belege dieses Lieferanten künftig automatisch auf dieses Konto buchen"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Als Regel speichern
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter158: Foto-Zoom-Dialog */}
      <ReceiptZoomDialog
        receipt={zoomReceipt}
        blobUrl={zoomBlobUrl}
        onClose={closeZoom}
        onNoteSaved={loadReceipts}
      />

      {/* Iter249: Beilagen (Büroklammer) – Vorschau, Art-Korrektur, Nachreichen */}
      <BillPreviewDialog
        docType="receipt"
        preview={attPreview.preview}
        onClose={attPreview.close}
        onSwitch={attPreview.open}
        onRemove={(doc, fileId) => attPreview.removeAttachment(doc, fileId, replaceReceipt)}
        onSetKind={(doc, fileId, kind) => attPreview.setKind(doc, fileId, kind, replaceReceipt)}
        onClassify={(doc) => attPreview.classify(doc, replaceReceipt)}
        onBillUpdated={(d) => { replaceReceipt(d); attPreview.updateBill(d); }}
        onAddAttachment={(doc) => { attPreview.close(); setAttachReceipt(doc); }}
      />
      <BillAttachmentUploadDialog docType="receipt" bill={attachReceipt} onClose={() => setAttachReceipt(null)} onUploaded={replaceReceipt} />

      {/* Iter158: 0.00-CHF-Warnung vor Buchung */}
      <ZeroAmountDialog
        receipt={zeroAmountReceipt}
        onClose={() => setZeroAmountReceipt(null)}
        onReanalyze={handleReanalyze}
        onForceBook={(id) => handlePostReceipt(id, { skipZeroCheck: true })}
      />

      {/* Iter159: Datum-fehlt-Warndialog vor Buchung */}
      <MissingDateDialog
        receipt={missingDateReceipt}
        onClose={() => setMissingDateReceipt(null)}
        onReanalyze={handleReanalyze}
        onSavedAndBook={async (id) => {
          await loadReceipts();
          setTimeout(() => handlePostReceipt(id, { skipDateCheck: true }), 200);
        }}
      />

      {/* Iter265: Zahlungsart-Dialog vor Buchung */}
      <ReceiptPayMethodDialog
        receipt={payDialogReceipt}
        onClose={() => setPayDialogReceipt(null)}
        onBook={doPostReceipt}
      />

      <DocCompareDialog
        open={!!compareDocs}
        onClose={() => setCompareDocs(null)}
        title={compareMode === "free" ? "Lupe — ausgewählte Belege im Vergleich" : "Duplikat-Lupe — Belege im Vergleich"}
        docs={compareDocs || []}
        footer={compareDocs && compareMode === "dup" && (
          <Button onClick={() => { const id = compareDocs.find((d) => d.accent)?.id || compareDocs[0].id; setCompareDocs(null); setMergerReceiptId(id); }} data-testid="duplicate-compare-to-merger">
            Zum KI-Merger — zusammenlegen
          </Button>
        )}
      />

      {/* Iter162: KI-Duplikat-Merger */}
      <DuplicateMergerDialog
        receiptId={mergerReceiptId}
        open={!!mergerReceiptId}
        onClose={() => setMergerReceiptId(null)}
        onMerged={loadReceipts}
      />

      {/* Iter163: Rückverbuchen-Assistent */}
      <UnbookAssistantDialog
        receipt={unbookReceipt}
        open={!!unbookReceipt}
        onClose={() => setUnbookReceipt(null)}
        onDone={loadReceipts}
      />

      {/* Iter163: Bulk-Duplikat-Merger */}
      <BulkMergerDialog
        companyId={companyId}
        open={bulkMergerOpen}
        onClose={() => setBulkMergerOpen(false)}
        onDone={loadReceipts}
      />

      {/* Iter168: Umbuchen-Dialog */}
      <RebookDialog
        receipt={rebookReceipt}
        open={!!rebookReceipt}
        onClose={() => setRebookReceipt(null)}
        onDone={loadReceipts}
        companyId={companyId}
      />
    </div>
  );
}
